import {
  Button,
  Card,
  CircularLoader,
  Icon,
  IconButton,
  Input,
  InputHandle,
  Stack,
  Title,
  useTheme,
  useToast
} from "@introist/react-foundation/v2";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Skeletons } from "../components/molecules";
import { loadThread, reportThread, resetThread, sendMessage } from "./api";
import { Message } from "./MessageCard";
import { ReportThreadModal } from "./ReportThreadModal";
import { NakedButton } from "../foundation";
import { LoadingTitle } from "./LoadingTitle";
import { uuid } from "short-uuid";

export const AssistantChat = () => {
  const [initialState, setInitialState] = useState<any>();

  useEffect(() => {
    loadThread().then(res => setInitialState(res));
  }, []);

  if (!initialState) return <Skeletons.Block height={300} width={500} />;

  return (
    <AssistantThread
      initialState={initialState}
      onReset={async () => {
        setInitialState(null);
        await resetThread().then(res => setInitialState(res));
      }}
    />
  );
};

const AssistantThread = ({
  initialState,
  onReset
}: {
  initialState: any;
  onReset: () => Promise<void>;
}) => {
  const toast = useToast();
  const { theme } = useTheme();

  const [state, setState] = useState<any>(initialState);
  const [messages, setMessages] = useState(state.messages || []);

  const [bottomDiv, setBottomDiv] = useState<HTMLDivElement | null>(null);

  const [inputRef, setInputRef] = useState<InputHandle | null>(null);
  const [input, setInput] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [reportOpen, setReportOpen] = useState(false);

  const onSubmit = useCallback(
    async (input: string, showTemp = true) => {
      setLoading(true);
      if (showTemp) {
        setMessages((prev: any[]) => [
          ...prev,
          { type: "human", data: { content: input, id: uuid() } }
        ]);
      }
      setInput("");
      setTimeout(() => {
        bottomDiv?.scrollIntoView();
      });

      try {
        await sendMessage(input).then((state: any) => {
          setState(state);
          setMessages(state.messages);
          setError("");
          setTimeout(() => {
            bottomDiv?.scrollIntoView();
            inputRef?.focus();
          });
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [inputRef, bottomDiv]
  );

  const onReport = useCallback(
    async (additionalInfo: string) => {
      await reportThread(additionalInfo)
        .then(() => {
          toast.success("Support request created successfully.");
          setReportOpen(false);
        })
        .catch(() => {
          toast.error("Failed to send request");
        });
    },
    [toast]
  );

  const isChatEnded = useMemo(() => {
    if (!messages || messages.length === 0) return false;
    const lastMessage = messages[messages.length - 1];
    return (
      lastMessage.type === "ai" && lastMessage.data?.content?.includes("Support request created")
    );
  }, [messages]);

  return (
    <>
      <Card>
        <Stack vertical gap="large" style={{ width: "600px", maxHeight: "90vh" }}>
          <Stack vertical>
            <Stack key="header" style={{ marginBottom: "var(--spacing-large)" }}>
              <Title variant="bold">Chat with Assistant</Title>
              <div style={{ marginLeft: "auto" }}>
                <IconButton
                  icon="recurring"
                  onClickWithLoading={() => onReset()}
                  tooltip="Start over"
                >
                  <Title>Start over</Title>
                </IconButton>
              </div>
            </Stack>
          </Stack>
          <Stack
            vertical
            gap="medium"
            style={{
              overflowY: "auto",
              flexGrow: 1,
              margin: "calc(var(--spacing-large) * -1)",
              padding: "var(--spacing-large)"
            }}
          >
            {messages.map((m: any) => (
              <Message message={m} key={m.data.id} />
            ))}
            {loading && (
              <Stack style={{ padding: "0 var(--spacing-medium)" }}>
                <Icon name="robot" />
                <LoadingTitle>Thinking</LoadingTitle>
              </Stack>
            )}
            <div style={{ float: "left", clear: "both" }} ref={setBottomDiv}></div>
          </Stack>
          <Stack vertical gap="small" style={{ marginTop: "var(--spacing-small)" }}>
            {isChatEnded && (
              <Button variant="outlined" onClick={() => onReset()}>
                Start new conversation
              </Button>
            )}
            {!isChatEnded && (
              <>
                <Input
                  ref={setInputRef}
                  autoFocus
                  size="small"
                  value={input}
                  onChange={setInput}
                  placeholder="What would you like to ask?"
                  disabled={loading}
                  error={!!error}
                  onKeyUp={e => {
                    if (e.key === "Enter") onSubmit(input);
                  }}
                  endAdornment={
                    loading ? (
                      <CircularLoader size="small" />
                    ) : (
                      <IconButton icon="arrowRight" onClick={() => onSubmit(input)} />
                    )
                  }
                />
                <Stack vertical gap="none" style={{ marginTop: "var(--spacing-small)" }}>
                  <Title small>
                    This chatbot answers questions using Introist Knowledge Base and cannot take
                    actions yet.
                  </Title>
                  <Title small>Please avoid sharing sensitive or personal information.</Title>
                  <Stack gap="xSmall">
                    <Title small>Need human?</Title>
                    <NakedButton onClick={() => setReportOpen(true)}>
                      <Title small variant="bold" color={theme.palette.foreground.default}>
                        Send request to human
                      </Title>
                    </NakedButton>
                  </Stack>
                </Stack>
              </>
            )}
            {error && (
              <Stack>
                <Icon name="warning" color="red" />
                <Title color={theme.palette.danger.default}>{error}</Title>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>
      <ReportThreadModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={onReport}
      />
    </>
  );
};
